import type { BalanceType } from '../../../api/fetchers/userInfo/types';
import { BetStateBitMask, BetStateBitMaskTypes } from '../../../logger/bitMasks/BetStateBitMask';
import { Logger } from '../../../logger/Logger';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import type { AutoplaySliceType } from '../../../store/slices/autoplay/types';
import { getAutoplayStopToastKey } from '../../../store/slices/autoplay/utils';
import type { BetsSliceType, IBetLimits, IBetSpots } from '../../../store/slices/bets/types';
import { selectIsBonusCampaignActive } from '../../../store/slices/bonusPromotions/selectors';
import { toastActions } from '../../../store/slices/toast/slice';
import { countTotalBet, getMinimalBet, isFreeBetPlaced } from '../../../utils/bets';

import { BaseCoreGameCommunicationProcessor } from './BaseCoreGameCommunicationProcessor';

export abstract class CoreLowBalanceGameCommunicationProcessor extends BaseCoreGameCommunicationProcessor {
  public checkLowBalanceAndUpdateState(
    balance?: BalanceType,
    freeChipBalance?: number,
    betLimits: IBetLimits | null = null
  ): boolean {
    const minBetSize = getMinimalBet(betLimits);
    const currBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
    const isLowBalance = currBalance < minBetSize && (freeChipBalance ?? 0) < minBetSize;

    if (isLowBalance) {
      BetStateBitMask.apply(BetStateBitMaskTypes.InsufficientFunds);
    }
    return isLowBalance;
  }

  public checkAutoplayAvailability(
    balance: BalanceType | undefined,
    bets: BetsSliceType,
    autoplay: AutoplaySliceType,
    freeChipBalance?: number
  ): boolean {
    if (autoplay.isInProgress) {
      let isAutoplayInProgress = true;
      const totalBet = this.getTotalBet(autoplay.savedBet?.betSpots);

      const savedBetSpots = Object.keys(autoplay.savedBet?.betSpots ?? {}).filter(
        (el) => (autoplay.savedBet?.betSpots?.[el]?.value ?? 0) > 0
      );
      const allBetsUnavailable = savedBetSpots.every((r) => bets.disabledBetSpots.includes(r));

      if (allBetsUnavailable) {
        this.store.dispatch(autoplayActions.stop());
        this.store.dispatch(toastActions.createToast('AUTOPLAY_PREFERENCES_STOPPED'));
        Logger.logEvent('autoplay_stop', {
          additional: 'BetsUnavailable',
        });
        isAutoplayInProgress = false;
      }

      const casinoBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
      const isFreeBet = isFreeBetPlaced(autoplay.savedBet?.betSpots);
      const currBalance = isFreeBet ? freeChipBalance ?? 0 : casinoBalance;
      const state = this.store.getState();
      const isBonusCampaignActive = selectIsBonusCampaignActive(state);
      if (totalBet > currBalance) {
        this.store.dispatch(autoplayActions.stop());
        const autoplayStopToastKey = getAutoplayStopToastKey(isBonusCampaignActive, isFreeBet);
        this.store.dispatch(toastActions.createToast(autoplayStopToastKey));

        Logger.logEvent('autoplay_stop', { additional: 'NoMoney' });
        isAutoplayInProgress = false;
      }
      return isAutoplayInProgress;
    }
    return false;
  }

  private getTotalBet(betSpots?: IBetSpots): number {
    const digits = this.store.getState().gameState.currencyInfo?.digitsNumber ?? 0;
    return countTotalBet(betSpots ?? {}, digits);
  }
}
